import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import { useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { DeacParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { deacService } from '../../../services/central-api/deac';
import VacancyTagModal from './VacancyTagModal';
import DeleteVacancyTagModal from './DeleteVacancyTagModal';
import Toast from 'react-native-toast-message';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';

export default function VacancyTags({ navigation }: { navigation: Navigation; }) {
    const { theme } = useStyles();
    const route = useRoute<RouteProp<DeacParamList, 'VacancyTags'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DeacParamList['VacancyTags']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedVacancyTag, setSelectedVacancyTag] = useState<VacancyTag | undefined>(undefined);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacVacanciesTags', filters],
        queryFn: () => deacService.getVacancyTags(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch, isError, error } = queryResult;

    useEffect(() => {
        if (isError) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }, [error, isError]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('vacancyTags')}
                actions={<CustomButton
                    text={translate('registerTag')}
                    onPress={() => {
                        setSelectedVacancyTag(undefined);
                        setIsModalVisible(true);
                    }}
                />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredTags')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('tag'),
                                cell: row => row.name,
                            },
                            {
                                name: translate('color'),
                                cell: row => <View
                                    style={{
                                        backgroundColor: row.color,
                                        width: 24,
                                        height: 24,
                                        borderRadius: 9999,
                                        borderWidth: 1,
                                        borderColor: theme.colors.neutral['300']
                                    }}
                                />,
                                grow: 4,
                            },
                            {
                                name: '',
                                cell: row => {
                                    const actions: Action[] = [
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedVacancyTag(row);
                                                setIsModalVisible(true);
                                            }
                                        },
                                        {
                                            content: translate('delete'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedVacancyTag(row);
                                                setIsDeleteModalVisible(true);
                                            },
                                        }
                                    ];

                                    return (
                                        <DropdownButton actions={actions} />
                                    );
                                },
                                wrap: true,
                                button: true
                            }
                        ]}
                        data={data?.rows || []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={data?.count || 0}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                            navigation.setParams({ ...route.params, page: page + 1 });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                            navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                        }}
                    />}
                />
            </Card>
            <VacancyTagModal
                selectedVacancyTagId={selectedVacancyTag?.id}
                onConfirm={refetch}
                isModalVisible={isModalVisible}
                setModalVisible={setIsModalVisible}
            />
            {selectedVacancyTag && <DeleteVacancyTagModal
                onConfirm={refetch}
                selectedVacancyTag={selectedVacancyTag}
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
            />}
        </Container>
    );
}
